import {
  AlertTriangle,
  Check,
  Loader2,
  RefreshCw,
  Share2,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react'
import type { PublishButtonState } from '@/features/moderation/lib/publishButtonState'
import type { ModerationState } from '@/features/moderation/types'
import { fromVisibility } from '@/features/moderation/lib/visibility'
import { ACCESS_META } from '@/features/instructions/lib/accessMeta'
import type { SaveStatus } from '../../editorSlice'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

const BASE =
  'flex items-center gap-1.5 rounded-full px-2 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 lg:px-3'
const BRAND = `${BASE} bg-brand text-brand-foreground hover:bg-brand-hover`
const WARNING = `${BASE} border border-warning/40 text-warning`
const OUTLINE = `${BASE} border border-border text-muted-foreground hover:text-foreground`

export interface PublishButtonView {
  label: string
  className: string
  Icon: LucideIcon
  spin?: boolean
}

type LabelKey =
  | 'publish'
  | 'statusUnknown'
  | 'pending'
  | 'rejected'
  | 'failed'
  | 'live'
  | 'update'
  | 'updateRejected'

/** Подпись читается в момент обращения — на языке интерфейса (вызывающий подписан на язык). */
const view = (key: LabelKey, rest: Omit<PublishButtonView, 'label'>): PublishButtonView => ({
  ...rest,
  get label() {
    return messagesNow(messages)[key]
  },
})

export const PUBLISH_BUTTON_VIEW = new Map<PublishButtonState, PublishButtonView>([
  ['unknown', view('publish', { className: BRAND, Icon: Share2 })],
  ['error', view('statusUnknown', { className: WARNING, Icon: AlertTriangle })],
  ['draft', view('publish', { className: BRAND, Icon: Share2 })],
  ['pending', view('pending', { className: OUTLINE, Icon: Loader2, spin: true })],
  [
    'rejected',
    view('rejected', {
      className: `${BASE} border border-destructive/40 text-destructive`,
      Icon: ShieldAlert,
    }),
  ],
  ['failed', view('failed', { className: WARNING, Icon: AlertTriangle })],
  ['live', view('live', { className: OUTLINE, Icon: Check })],
  ['update', view('update', { className: BRAND, Icon: RefreshCw })],
  [
    'update_rejected',
    view('updateRejected', {
      className: `${BASE} border border-destructive/40 text-destructive`,
      Icon: ShieldAlert,
    }),
  ],
])

/** Причины, по которым погашенная кнопка сохранения ждёт: у `pending`/`rejected`/
 *  `failed`/`error` окно только показывает статус, сохранение ему не мешает.
 *  `update` тоже не ждёт (решение заказчика): черновик есть — кнопка горит
 *  всё время, а не мигает на каждом автосохранении. Она только открывает окно;
 *  отправку несохранённого блока гасит уже «Обновить» внутри окна. */
export const saveBlocksPublish = (state: PublishButtonState): boolean =>
  state === 'draft' || state === 'live'

/** Подсказка — ровно та причина, по которой кнопка погашена; у живой — нет. */
export const publishButtonTitle = (
  state: PublishButtonState,
  saveStatus: SaveStatus,
  isCourseAuthor: boolean,
): string | undefined => {
  const m = messagesNow(messages)
  if (!isCourseAuthor) return m.noRights
  if (state === 'unknown') return m.loadingStatus
  if (!saveBlocksPublish(state)) return undefined
  if (saveStatus === 'saving') return m.waitSave
  if (saveStatus === 'error') return m.saveFailed
  return undefined
}

/** Состояния опубликованного курса — у них значок места публикации. */
const LIVE_STATES = new Set<PublishButtonState>(['live', 'update', 'update_rejected'])

/**
 * Значок опубликованного курса — ГДЕ он опубликован: ссылка или каталог
 * (`ACCESS_META`, тот же значок, что в селекте доступа), а не общая галочка,
 * и так же у «Обновить» и «Обновление отклонено» (решение заказчика, без
 * цветных точек: состояние говорят подпись и цвет кнопки).
 */
export const publishButtonIcon = (
  state: PublishButtonState,
  moderation: ModerationState | undefined,
  fallback: LucideIcon,
): LucideIcon =>
  LIVE_STATES.has(state) && moderation !== undefined && moderation.visibility !== 'unpublished'
    ? ACCESS_META[fromVisibility(moderation.visibility)].icon
    : fallback

/** Проверка обновления живого курса — «Проверяется…», а не общее «На проверке». */
export const publishButtonLabel = (
  state: PublishButtonState,
  moderation: ModerationState | undefined,
  fallback: string,
): string =>
  state === 'pending' && moderation?.review?.kind === 'update'
    ? messagesNow(messages).reviewingUpdate
    : fallback

/**
 * Проверять ли замечания на кнопке шапки: первая и повторная публикация. Но не
 * у курса с черновиком правок при одобренной версии (курс закрыли, не отправив
 * обновление): там, как у живого курса, замечания черновика живут на кнопке
 * «Обновить» внутри окна, а шапка лишь открывает окно — доступ меняется у
 * одобренной версии, и пустое видео черновика ему не помеха.
 */
export const checksPublishIssues = (
  state: PublishButtonState,
  moderation: ModerationState | undefined,
): boolean =>
  (state === 'draft' || state === 'rejected' || state === 'failed') &&
  (moderation?.draftId ?? null) === null
